<template>
  <div class="card-root">
    <div class="card-link" role="button" @click="openModal">
      <div class="card" :class="{ 'no-media': !hasMedia, 'has-tech': technologies && technologies.length }">
        <div v-if="hasMedia" class="card-preview">
          <div class="preview-media-wrapper">
            <template v-if="mediaList[0].type === 'image'">
              <img
                  class="preview-media"
                  :src="mediaList[0].src"
                  :alt="(title || 'aperçu') + ' - première image'"
              />
            </template>
            <template v-else-if="mediaList[0].type === 'video'">
              <video
                  ref="previewVideo"
                  class="preview-media"
                  :src="mediaList[0].src"
                  autoplay
                  muted
                  loop
                  playsinline
                  preload="metadata"
                  aria-label="aperçu vidéo"
              ></video>
            </template>
            <template v-else-if="mediaList[0].type === 'pdf'">
              <iframe
                  class="preview-media"
                  :src="mediaList[0].src"
                  title="aperçu PDF"
              ></iframe>
            </template>
          </div>
        </div>

        <h2 style="color: #00f0ff; margin: 0;">{{ title }}</h2>
        <p v-if="subTitle">{{ subTitle }}</p>

        <div v-if="technologies && technologies.length" class="card-tech" aria-hidden="true">
          <div class="card-tech-list">
            <span v-for="(tech, i) in firstTwoTechnologies"
                  :key="'card-tech-' + i"
                  class="tech-chip tech-chip--card">{{ tech }}</span>
            <span v-if="technologies.length > 2"
                  class="tech-chip tech-chip--count"
                  :aria-label="(technologies.length - 2) + ' technologies supplémentaires'">
              +{{ technologies.length - 2 }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <transition name="overlay-fade">
      <div v-if="overlayVisible" class="modal-overlay" @click.self="closeModal">
        <transition name="modal-zoom" appear @after-leave="onModalAfterLeave">
          <div v-if="modalVisible" class="modal" role="dialog" aria-modal="true" :aria-label="title || 'Détails'">
            <div class="modal-header">
              <h2 style="color: #00f0ff;">{{ title }}</h2>
              <button class="close-btn" @click.stop="closeModal" aria-label="Fermer">×</button>
            </div>

            <div class="modal-body" :class="{ 'no-media': !hasMedia }">
              <div v-if="hasMedia" class="gallery">
                <div class="main-media-wrapper">
                  <button v-if="mediaList.length > 1" class="nav left" @click.stop="prev" aria-label="Média précédent">
                    ‹
                  </button>

                  <template v-if="currentItem && currentItem.type === 'image'">
                    <img :src="currentItem.src" :alt="title || 'image'" class="main-media"/>
                  </template>
                  <template v-else-if="currentItem && currentItem.type === 'video'">
                    <video
                        class="main-media"
                        :src="currentItem.src"
                        controls
                        preload="metadata"
                        playsinline
                    ></video>
                  </template>
                  <template v-else-if="currentItem && currentItem.type === 'pdf'">
                    <iframe
                        class="main-media"
                        :src="currentItem.src"
                        title="Aperçu PDF"
                    ></iframe>
                  </template>

                  <button v-if="mediaList.length > 1" class="nav right" @click.stop="next" aria-label="Média suivant">
                    ›
                  </button>
                </div>

                <div v-if="mediaList.length > 1" class="thumbnails">
                  <button
                      v-for="(item, idx) in mediaList"
                      :key="idx"
                      class="thumb-btn"
                      :class="{ active: idx === currentIndex }"
                      @click.stop="goTo(idx)"
                      :aria-label="'Aller au média ' + (idx + 1)"
                  >
                    <template v-if="item.type === 'image'">
                      <img :src="item.src" :alt="'miniature ' + (idx + 1)"/>
                    </template>
                    <template v-else-if="item.type === 'video'">
                      <video :src="item.src" muted preload="metadata"></video>
                      <span class="play-badge" aria-hidden="true">▶</span>
                    </template>
                    <template v-else-if="item.type === 'pdf'">
                      <div class="pdf-thumb">📄</div>
                    </template>
                  </button>
                </div>
              </div>

              <div v-if="description || (technologies && technologies.length)" class="content-area">
                <template v-if="description">
                  <div class="description-text" v-html="description"></div>
                </template>

                <template v-if="technologies && technologies.length">
                  <h3 class="tech-title">Technologies</h3>
                  <div class="tech-list">
                    <span v-for="(tech, i) in technologies" :key="i" class="tech-chip">{{ tech }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="modal-actions">
              <button class="primary-btn" @click="closeModal">Fermer</button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import {isPdfSrc, isVideoSrc, toPortfolioMediaList} from "@/utils/portfolioMedia";

export default {
  name: 'CardPortfolio',

  props: {
    title: String,
    subTitle: String,
    description: String,
    to: String,
    images: {
      type: Array,
      default: () => []
    },
    technologies: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      overlayVisible: false,
      modalVisible: false,
      currentIndex: 0
    }
  },

  computed: {
    hasMedia() {
      return Array.isArray(this.images) && this.images.length > 0;
    },

    mediaList() {
      return toPortfolioMediaList(this.images);
    },

    currentItem() {
      return this.mediaList[this.currentIndex] || null;
    },

    firstTwoTechnologies() {
      const list = Array.isArray(this.technologies) ? this.technologies : [];
      return list.slice(0, 2);
    }
  },

  methods: {
    openModal() {
      this.pausePreviewVideo();
      this.overlayVisible = true;
      document.body.classList.add('no-scroll');
      this.$nextTick(() => {
        this.modalVisible = true;
        this.currentIndex = 0;
      });
    },

    closeModal() {
      this.modalVisible = false;
    },

    onModalAfterLeave() {
      this.overlayVisible = false;
      this.resumePreviewVideo();
      document.body.classList.remove('no-scroll');
    },

    next() {
      if (!this.hasMedia) return;
      this.currentIndex = (this.currentIndex + 1) % this.mediaList.length;
    },

    prev() {
      if (!this.hasMedia) return;
      this.currentIndex = (this.currentIndex - 1 + this.mediaList.length) % this.mediaList.length;
    },

    goTo(index) {
      if (!this.hasMedia) return;
      this.currentIndex = index;
    },

    handleEsc(e) {
      if (e.key === 'Escape' && this.modalVisible) this.closeModal();
    },

    isVideoSrc(src) {
      return isVideoSrc(src);
    },

    isPdfSrc(src) {
      return isPdfSrc(src);
    },

    pausePreviewVideo() {
      const v = this.$refs.previewVideo;
      if (v && typeof v.pause === 'function') v.pause();
    },

    resumePreviewVideo() {
      const v = this.$refs.previewVideo;
      if (v && typeof v.play === 'function') {
        v.muted = true;
        v.play().catch(() => {
        });
      }
    }
  },

  mounted() {
    window.addEventListener('keydown', this.handleEsc);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEsc);
    document.body.classList.remove('no-scroll');
  }
}
</script>

<style scoped>
:global(body.no-scroll) {
  overflow: hidden;
}

.card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  cursor: pointer;
}

.card {
  position: relative;
  background-color: #323336;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.75rem;
  box-sizing: border-box;
  height: 320px;
  overflow: hidden;
}

.card:hover {
  box-shadow: 0 0 20px #00f0ff, 0 0 30px #00f0ff66;
}

@media (max-width: 650px) {
  .card {
    height: 280px;
  }
}

.card.no-media {
  justify-content: center;
}

.card-preview {
  margin: -0.5rem -0.5rem 0;
  border-radius: 14px;
  overflow: hidden;
}

.preview-media-wrapper {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #000;
  height: 150px;
}

.preview-media {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
  border: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
}

.modal {
  background-color: #323336;
  color: #ffffff;
  width: 70vw;
  max-width: none;
  min-width: 300px;
  border-radius: 20px;
  box-shadow: 0 0 20px #00f0ff, 0 0 30px #00f0ff66;
  transform-origin: center;
  will-change: transform, opacity;
  transform: translateZ(0);
  padding: 1rem 1.5rem;
  max-height: calc(100dvh - 64px);
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {
  .modal {
    width: 92vw;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .75rem;
  flex: 0 0 auto;
}

.close-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: .75rem;
  flex: 0 0 auto;
}

.primary-btn {
  background-color: #00f0ff;
  color: #000;
  border: none;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  cursor: pointer;
}

.primary-btn:hover {
  box-shadow: 0 0 10px #00f0ff, 0 0 20px #00f0ff66;
}

.modal-body {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.modal-body.no-media {
  display: block;
}

.gallery {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
}

.main-media-wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.main-media {
  width: 100%;
  height: 500px;
  object-fit: contain;
  display: block;
  background: #000;
  border: none;
}

.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav.left {
  left: 8px;
}

.nav.right {
  right: 8px;
}

.thumbnails {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 8px;
  flex-wrap: nowrap;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.thumb-btn {
  position: relative;
  background: transparent;
  padding: 0;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  width: 64px;
  height: 64px;
  flex: 0 0 auto;
  opacity: 0.8;
  border: 2px solid transparent;
}

.thumb-btn img,
.thumb-btn video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb-btn.active {
  opacity: 1;
  border-color: #00f0ff;
}

.play-badge {
  position: absolute;
  inset: auto 4px 4px auto;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 6px;
}

.pdf-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #444;
  color: #fff;
  font-size: 1.2rem;
  width: 100%;
  height: 100%;
}

.content-area {
  background: #2a2b2e;
  border-radius: 12px;
  padding: 1rem;
  min-width: 0;
}

.description-text {
  margin-top: 0;
  white-space: pre-wrap;
}

.description-text p {
  margin: 0;
}

.tech-title {
  margin: 1rem 0 0.5rem;
  color: #00f0ff;
}

.tech-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.6rem;
}

.tech-chip {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border: 2px solid #00f0ff;
  color: #e6faff;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.06);
  box-shadow: 0 0 8px #00f0ff33 inset;
  font-size: 0.9rem;
  white-space: nowrap;
}

.modal-body.no-media .content-area {
  max-height: none;
}

@media (max-width: 900px) {
  .modal-body {
    grid-template-columns: 1fr;
  }

  .main-media {
    height: 220px;
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.22s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.modal-zoom-enter-active {
  transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.28s ease-out;
}

.modal-zoom-leave-active {
  transition: transform 0.24s cubic-bezier(0.36, 0, 0.66, -0.56), opacity 0.2s ease-in;
}

.modal-zoom-enter-from,
.modal-zoom-leave-to {
  transform: scale(0.7);
  opacity: 0;
}

.card.has-tech {
  padding-bottom: 3.5rem;
}

.card-tech {
  position: absolute;
  right: 12px;
  bottom: 12px;
}

.card-tech-list {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.tech-chip--card {
  font-size: 0.85rem;
  padding: 0.3rem 0.65rem;
  background: rgba(0, 240, 255, 0.08);
}

.tech-chip--count {
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.8rem;
}

@media (max-width: 360px) {
  .tech-chip--card {
    font-size: 0.8rem;
    padding: 0.25rem 0.55rem;
  }

  .tech-chip--count {
    width: 24px;
    height: 24px;
    min-width: 24px;
    font-size: 0.72rem;
  }
}
</style>
